import { FC, SyntheticEvent, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { ListItem, Typography, Popper, IconButton } from "@mui/material";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { ClearIcon } from "@mui/x-date-pickers";

interface ModelOption {
  label: string;
  value: string | null;
}

const MODEL_OPTIONS: ModelOption[] = [
  { label: "All events", value: null },
  { label: "Smoke", value: "SMOKE" },
  { label: "Fire", value: "FIRE" },
  { label: "Hard hat", value: "HARDHAT" },
  { label: "Liquid leak", value: "LIQUIDLEAK" },
  { label: "Gas leak", value: "GASLEAK" },
];

interface EventSelectProps {
  value: string | null;
  onChange: (value: string | null) => void;
  disabled?: boolean;
  size?: "small" | "medium";
}

const EventSelect: FC<EventSelectProps> = ({
  value,
  onChange,
  disabled = false,
  size = "small",
}) => {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState<string | null>(null);

  const selectedOption =
    MODEL_OPTIONS.find(option => option.value === value) ?? MODEL_OPTIONS[0];

  const handleChange = (_: SyntheticEvent, newOption: ModelOption | null) => {
    if (!disabled) {
      onChange(newOption?.value ?? null);
    }
  };

  const handleClear = () => {
    if (!disabled) {
      onChange(null);
    }
  };

  return (
    <Autocomplete
      fullWidth
      size={size}
      options={MODEL_OPTIONS}
      getOptionLabel={option => option.label}
      isOptionEqualToValue={(option, val) => option.value === val.value}
      value={selectedOption}
      onChange={handleChange}
      disabled={disabled}
      popupIcon={<ExpandMore />}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => {
        setOpen(false);

        setHovered(null);
      }}
      disableClearable
      inputValue={open ? "" : selectedOption.label}
      renderOption={(props, option) => (
        <ListItem
          {...props}
          onMouseEnter={() => setHovered(option.label)}
          onMouseLeave={() => setHovered(null)}
          sx={{
            bgcolor: hovered === option.label ? "inherit" : "transparent",
            "&.Mui-focused": { bgcolor: "transparent" },
            "&[aria-selected='true']": { bgcolor: "transparent" },
          }}
        >
          <Typography>{option.label}</Typography>
        </ListItem>
      )}
      renderInput={params => (
        <TextField
          {...params}
          placeholder="Select events"
          variant="outlined"
          label={selectedOption.value === null ? undefined : "Events"}
          InputLabelProps={{
            shrink: selectedOption.value !== null,
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {value && !disabled && (
                  <IconButton onClick={handleClear} size={size} sx={{ p: 0 }}>
                    <ClearIcon fontSize={size} />
                  </IconButton>
                )}
                {params.InputProps.endAdornment}
              </>
            ),
            sx: {
              "& input": {
                color:
                  selectedOption.value === null
                    ? "otherTextTertiary.main"
                    : "inherit",
              },
            },
          }}
        />
      )}
      PopperComponent={props => (
        <Popper
          {...props}
          placement="bottom-start"
          modifiers={[{ name: "offset", options: { offset: [0, 8] } }]}
        />
      )}
    />
  );
};

export default EventSelect;
